import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Progress } from '@qodo/design-system';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@qodo/design-system/ui/popover';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { FEATURE_FLAGS } from '../../consts/features';
import { useIsFeatureEnabledQuery } from '../../network/queries/useIsFeatureEnabledQuery';
import { useTaskTrackerSummaryQuery } from '../../network/queries/useTaskTrackerSummaryQuery';
import { ROUTES } from '../../routes/consts';
import { cn } from '../../utils/className';
import { RowTone, TaskRow, toWidgetModel } from './utils';

// Motion distinguishes "work is happening" from "work is parked": running
// pulses in the accent colour, queued pulses grey (waiting, not stalled), and
// the two terminal states sit still.
//
// `bg-primary` is the same token the progress bar fills with, so a running row
// reads as one colour. It replaces `fill-general-primary-strong`, which is not
// a token this design system defines: the class resolved to nothing and the
// running and queued dots rendered invisible.
const DOT_TONE_CLASSES: Record<RowTone, string> = {
  running: 'bg-primary animate-pulse motion-reduce:animate-none',
  queued:
    'bg-fill-interactive-neutral-idle animate-pulse motion-reduce:animate-none',
  done: 'bg-text-tag-positive-idle',
  failed: 'bg-text-tag-negative-idle',
};

const NEGATIVE_SEGMENTS = new Set(['failed', 'timed-out']);

const TaskTypeRow = ({ row }: { row: TaskRow }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Only the states a person can act on get spelled out. Running and queued
  // counts used to sit inline beside the title and wrapped into a three-line
  // block of coloured fragments; the pulsing dot and the done/total fraction
  // already say that work is moving, so the text is reserved for failures.
  const problems = row.segments.filter(([segment]) =>
    NEGATIVE_SEGMENTS.has(segment),
  );

  const toneLabel = t(`background-tasks.tone.${row.tone}`);

  const outcome = row.prsIndexed
    ? t('background-tasks.prs-indexed', {
        indexed: row.prsIndexed.indexed.toLocaleString(),
        total: row.prsIndexed.total.toLocaleString(),
      })
    : row.rulesImported !== undefined
      ? t('background-tasks.rules-imported', { count: row.rulesImported })
      : null;

  return (
    // The row opens the Repositories page — the closest per-repo view today;
    // per-repo indexing detail inside the widget is future DES-191 scope.
    <li>
      <button
        type="button"
        onClick={() => navigate(ROUTES.REPOSITORIES)}
        className="group -mx-2 flex w-full flex-col gap-2 rounded-md p-2 text-left transition-colors hover:bg-fill-bg-neutral-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-action-emphasis-idle/40"
      >
        <div className="flex w-full items-center gap-2">
          <span
            // The dot is the row's only state indicator now that the running
            // and queued counts are gone, so it carries a text equivalent
            // rather than relying on colour alone.
            title={toneLabel}
            className={cn(
              'size-2 shrink-0 rounded-full',
              DOT_TONE_CLASSES[row.tone],
            )}
          />
          <span className="sr-only">{toneLabel}</span>
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-text-general-neutral-strong">
            {t(`background-tasks.types.${row.taskType}`, {
              defaultValue: row.taskType.replaceAll('_', ' '),
            })}
          </span>
          <span className="shrink-0 text-xs tabular-nums text-text-general-neutral-mild">
            {t('background-tasks.repos-fraction', {
              done: row.repos.done.toLocaleString(),
              total: row.repos.total.toLocaleString(),
            })}
          </span>
          <ChevronRight
            aria-hidden
            className="size-3.5 shrink-0 text-text-general-neutral-mild transition-colors group-hover:text-text-general-neutral-strong"
          />
        </div>
        <Progress
          value={row.progress}
          className="h-1 bg-fill-interactive-neutral-idle"
        />
        {(outcome || problems.length > 0) && (
          <div className="flex w-full items-baseline gap-2 text-xs">
            {outcome && (
              <span className="min-w-0 truncate text-text-general-neutral-mild">
                {outcome}
              </span>
            )}
            {problems.length > 0 && (
              <span className="ml-auto shrink-0 text-text-tag-negative-idle">
                {problems
                  .map(([segment, n]) =>
                    t(`background-tasks.segments.${segment}`, { n }),
                  )
                  .join(' · ')}
              </span>
            )}
          </div>
        )}
      </button>
    </li>
  );
};

const StatusIcon = ({ isBusy, hasFailures }: StatusState) => {
  if (isBusy) {
    return (
      <Loader2
        aria-hidden
        className="size-4 shrink-0 animate-spin text-text-general-neutral-strong motion-reduce:animate-none"
      />
    );
  }

  if (hasFailures) {
    return (
      <AlertTriangle
        aria-hidden
        className="size-4 shrink-0 text-text-tag-negative-idle"
      />
    );
  }

  return (
    <CheckCircle2
      aria-hidden
      className="size-4 shrink-0 text-text-tag-positive-idle"
    />
  );
};

type StatusState = { isBusy: boolean; hasFailures: boolean };

/**
 * Background-jobs status for the app header: a compact trigger that opens the
 * per-task-type detail in a popover. It lives in the header rather than
 * floating over the page because the detail panel used to sit bottom-right,
 * exactly where tables put pagination and forms put their submit button, and
 * its open state persisted across navigations.
 *
 * Rolled out per workspace via the background_tasks_widget feature flag;
 * renders only while the workspace has tracked tasks, and on older backends
 * without the summary endpoint it renders nothing.
 */
export const BackgroundTasksWidget = () => {
  const { t } = useTranslation();
  const { data: isWidgetEnabled } = useIsFeatureEnabledQuery(
    FEATURE_FLAGS.BACKGROUND_TASKS_WIDGET,
  );
  const { data, isError } = useTaskTrackerSummaryQuery({
    enabled: !!isWidgetEnabled,
  });

  const model = toWidgetModel(data);

  const isHidden = !isWidgetEnabled || isError || !model.anythingTracked;

  if (isHidden) {
    return null;
  }

  const isBusy = model.runningTasks > 0;
  const hasFailures = model.failedTasks > 0;

  const title = isBusy
    ? t('background-tasks.running-title', { count: model.runningTasks })
    : hasFailures
      ? t('background-tasks.failed-title', { count: model.failedTasks })
      : t('background-tasks.done-title');

  // Idle still needs a word or two: a lone check icon in the header gives no
  // clue what it belongs to. The status text carries the state when there is
  // one, and names the control when there is not.
  const triggerLabel =
    isBusy || hasFailures ? title : t('background-tasks.idle-label');

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('background-tasks.aria-label')}
          className="group flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-fill-bg-neutral-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-action-emphasis-idle/40"
        >
          <StatusIcon isBusy={isBusy} hasFailures={hasFailures} />
          <span
            role="status"
            className={cn(
              'hidden text-sm font-medium text-text-general-neutral-strong sm:inline',
              !isBusy && hasFailures && 'text-text-tag-negative-idle',
            )}
          >
            {triggerLabel}
          </span>
          <ChevronDown
            aria-hidden
            className="size-3.5 shrink-0 text-text-general-neutral-mild transition-transform duration-200 group-data-[state=open]:rotate-180 motion-reduce:transition-none"
          />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        // The design-system popover ships a bare `border` with no color
        // utility, so it inherits currentColor and reads as a white edge on
        // the dark theme; the panel tokens below are the card's real surface.
        className="w-[360px] max-w-[calc(100vw-2rem)] border-border-panel-neutral bg-fill-panel-neutral p-0"
      >
        <div className="flex flex-col gap-0.5 border-b border-border-panel-neutral p-4">
          <span
            className={cn(
              'text-sm font-semibold text-text-general-neutral-strong',
              !isBusy && hasFailures && 'text-text-tag-negative-idle',
            )}
          >
            {title}
          </span>
          <span className="text-xs text-text-general-neutral-mild">
            {t('background-tasks.subtitle')}
          </span>
        </div>
        <ul className="flex flex-col gap-4 p-4">
          {model.rows.map((row) => (
            <TaskTypeRow key={row.taskType} row={row} />
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
